/**
 * One session's access for one connected AI tool (TMMAPP-0142 L1–L3; TMMAPI-0183 U1): Off, Read
 * only, Read and write, saving at once. Read only is free; Read and write is a subscriber's, so for
 * a GUEST or NONE tier it runs through `useSubscriberAction()` (the purchase experience), never a
 * disabled option. Inside a dialog the host passes `onPurchase` (it closes first: dialogs never
 * stack, DLG-005). A server refusal shows under the control, through catalog copy. Read-only (an
 * archived session) shows the access as text.
 */
import { useState, type MouseEvent } from 'react';
import { Alert, Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { AgentAccessChoice } from '@/api/agentConnections';
import { errorCopy } from '@/api/errorCopy';
import { ErrorCodes, isApiError } from '@/api/errors';
import { SegmentedControl } from '@/components/SegmentedControl';
import { useOpenSubscribe } from '@/features/subscribe/subscribeOfferContext';
import { SubscriberActionHint, useSubscriberAction } from '@/session/useSubscriberAction';
import { resolveTokens } from '@/theme/theme';

const ACCESS_CHOICES: readonly AgentAccessChoice[] = ['OFF', 'READ', 'WRITE'];

interface AgentAccessControlProps {
  /** The group's accessible name ("Access for October budget"). */
  label: string;
  value: AgentAccessChoice;
  /** Saves the choice; throws on a refusal. */
  onChoose: (choice: AgentAccessChoice) => Promise<void>;
  readOnly?: boolean | undefined;
  /** Inside a dialog: the host closes, then opens the purchase (never stacked). */
  onPurchase?: (() => void) | undefined;
}

export function AgentAccessControl({
  label,
  value,
  onChoose,
  readOnly = false,
  onPurchase,
}: AgentAccessControlProps) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const openSubscribe = useOpenSubscribe();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function save(choice: AgentAccessChoice) {
    if (choice === value || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onChoose(choice);
    } catch (caught) {
      // A lapsed subscription the tier didn't show yet: the purchase opens, and the copy says why.
      // Inside a dialog the host closes it first (DLG-005).
      if (isApiError(caught) && caught.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        (onPurchase ?? openSubscribe)();
      }
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  const write = useSubscriberAction(() => void save('WRITE'), false, onPurchase);

  if (readOnly) {
    return (
      <Typography sx={{ fontSize: 13, color: tokens.text2 }} data-agent-access={value}>
        {t(`agentTools.access.${value}`)}
      </Typography>
    );
  }

  return (
    <Stack sx={{ gap: 0.75, minWidth: 0 }}>
      <SegmentedControl
        aria-label={label}
        value={value}
        busy={busy}
        options={ACCESS_CHOICES.map((choice) => ({
          value: choice,
          label: t(`agentTools.access.${choice}`),
          describedBy: choice === 'WRITE' ? write.descriptionId : undefined,
        }))}
        onChange={(choice, event) => {
          // A ToggleButton is a button: its click is the subscriber action's.
          if (choice === 'WRITE') write.run(event as MouseEvent<HTMLButtonElement>);
          else void save(choice);
        }}
      />
      <SubscriberActionHint id={write.descriptionId} description={write.description} />
      {error !== null && (
        <Alert severity="warning" data-agent-access-error="">
          {errorCopy(t, error, 'agentTools.saveError')}
        </Alert>
      )}
    </Stack>
  );
}
